import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Check, X, Star, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const addons = [
  { key: "api", price: "50" },
  { key: "accounting", price: "50" },
  { key: "quoteRequest", price: "50" },
  { key: "communication", price: "50" },
  { key: "payment", price: null },
];

export function PricingSection() {
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/packages`);
        setPackages(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch packages:", err);
        // Fallback static data
        setPackages([
          { key: "free", price: "0", limits: { products: 50, branches: 1, dealers: 5, suppliers: 1 }, highlighted: false, no_whatsapp: true, no_integrations: true },
          { key: "starter", price: "100", limits: { products: 200, branches: 5, dealers: 20, suppliers: 5 }, highlighted: false, no_whatsapp: false, no_integrations: false },
          { key: "pro", price: "300", limits: { products: 800, branches: 20, dealers: 200, suppliers: 20 }, highlighted: true, no_whatsapp: false, no_integrations: false },
          { key: "business", price: "750", limits: { products: 3600, branches: 80, dealers: 800, suppliers: 80 }, highlighted: false, no_whatsapp: false, no_integrations: false },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-sky-50/40 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#22A7D1]">
            {t("pricing.overline")}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-500">
            {t("pricing.subtitle")}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
            {packages.map((pkg) => (
              <div
                key={pkg.key}
                className={`relative rounded-2xl border p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl group ${
                  pkg.highlighted
                    ? "border-sky-300 bg-gradient-to-b from-sky-50/60 to-white shadow-lg shadow-sky-100/50"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`pricing-${pkg.key}`}
              >
                {pkg.highlighted && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-[#22A7D1] to-[#32AED6] rounded-full shadow-md shadow-sky-500/20">
                      <Star className="w-3 h-3 fill-white" />
                      {t("pricing.popular")}
                    </span>
                  </div>
                )}

                <h3 className="text-lg font-bold text-slate-900">
                  {t(`pricing.${pkg.key}`) !== `pricing.${pkg.key}` ? t(`pricing.${pkg.key}`) : pkg.key}
                </h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-5xl font-black text-slate-900 tracking-tighter">${pkg.price}</span>
                  <span className="text-sm text-slate-400 font-medium">/ {t("pricing.yearly")}</span>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-6" />

                <ul className="space-y-3">
                  {Object.entries(t("pricing.features")).map(([fKey, fLabel]) => {
                    const isDisabled =
                      (pkg.no_whatsapp && fKey === "messaging") ||
                      (pkg.no_integrations && fKey === "dealerPortal");
                    return (
                      <li key={fKey} className={`flex items-start gap-2.5 text-sm ${isDisabled ? "text-slate-400 line-through" : "text-slate-600"}`}>
                        {isDisabled ? (
                          <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                            <X className="w-3 h-3 text-slate-400" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-sky-50 flex items-center justify-center shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-sky-600" />
                          </div>
                        )}
                        {fLabel}
                      </li>
                    );
                  })}
                </ul>

                <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-6" />

                <div className="grid grid-cols-2 gap-2.5">
                  {[
                    { label: t("pricing.limits.products"), value: pkg.limits?.products },
                    { label: t("pricing.limits.branches"), value: pkg.limits?.branches },
                    { label: t("pricing.limits.dealers"), value: pkg.limits?.dealers },
                    { label: t("pricing.limits.suppliers"), value: pkg.limits?.suppliers },
                  ].map((limit, i) => (
                    <div key={i} className="text-center p-2.5 rounded-xl bg-slate-50/80 border border-slate-100 group-hover:border-slate-200 transition-colors">
                      <div className="text-xl font-black text-slate-900">{limit.value}</div>
                      <div className="text-[11px] text-slate-500 font-medium">{limit.label}</div>
                    </div>
                  ))}
                </div>

                <a
                  href="https://bayi.entegrepanel.net/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full mt-6 py-3 text-sm font-semibold text-center rounded-full transition-all duration-300 hover:-translate-y-0.5 ${
                    pkg.highlighted
                      ? "bg-gradient-to-r from-[#22A7D1] to-[#32AED6] text-white hover:shadow-lg hover:shadow-sky-500/25"
                      : "bg-slate-900 text-white hover:bg-slate-800 hover:shadow-md"
                  }`}
                  data-testid={`pricing-cta-${pkg.key}`}
                >
                  {pkg.price === "0" ? t("pricing.cta") : t("pricing.ctaStarter")}
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Add-ons */}
        <div className="mt-16">
          <h3 className="text-xl font-bold text-slate-900 text-center mb-8">
            {t("pricing.addons.title")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {addons.map((addon) => (
              <div
                key={addon.key}
                className="rounded-xl border border-slate-200 bg-white p-5 text-center hover:border-sky-200 hover:shadow-md transition-all group"
                data-testid={`addon-${addon.key}`}
              >
                <h4 className="text-sm font-semibold text-slate-900">{t(`pricing.addons.${addon.key}`)}</h4>
                {addon.price ? (
                  <div className="mt-2">
                    <span className="text-2xl font-black text-slate-900">${addon.price}</span>
                    <span className="text-xs font-medium text-slate-400"> / {t("pricing.yearly")}</span>
                  </div>
                ) : (
                  <div className="mt-2 text-sm font-semibold text-sky-600">{t("pricing.addons.soon")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
